import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, X, Star, Crown, Gem } from "lucide-react";
import { Link } from "react-router-dom";
const MembershipComparison = () => {
  const tiers = [{
    name: "Essential",
    price: "$149",
    icon: Star,
    color: "juv-terracotta",
    popular: false,
    description: "Perfect for getting started with regular treatments",
    credits: "12 credits/month",
    benefits: ["12 monthly credits", "10% discount on additional services", "Priority booking", "Rollover up to 24 credits", "Free consultations", "Member-only events"],
    limitations: ["No exclusive treatments", "Limited add-on discounts"]
  }, {
    name: "Signature",
    price: "$299",
    icon: Crown,
    color: "juv-deep-chestnut",
    popular: true,
    description: "Our most popular membership for regular treatment enthusiasts",
    credits: "24 credits/month",
    benefits: ["24 monthly credits", "15% discount on additional services", "Priority booking", "Rollover up to 48 credits", "Free consultations", "Member-only events", "Complimentary upgrades", "Guest privileges"],
    limitations: ["Limited access to premium treatments"]
  }, {
    name: "Transformational",
    price: "$499",
    icon: Gem,
    color: "juv-rose-clay",
    popular: false,
    description: "Ultimate luxury experience with unlimited benefits",
    credits: "40 credits/month",
    benefits: ["40 monthly credits", "20% discount on additional services", "VIP priority booking", "Unlimited credit rollover", "Free consultations", "Exclusive member events", "Complimentary upgrades", "Guest privileges", "Access to premium treatments", "Concierge service", "Free product samples"],
    limitations: []
  }];
  const features = ["Monthly Credits", "Additional Service Discount", "Priority Booking", "Credit Rollover", "Free Consultations", "Member Events", "Complimentary Upgrades", "Guest Privileges", "Premium Treatment Access", "Concierge Service", "Free Product Samples"];
  return <section className="py-20 bg-juv-cream-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-juv-deep-chestnut mb-6 font-serif">
            Choose Your Membership
          </h2>
          <p className="text-xl text-juv-deep-chestnut/70 max-w-3xl mx-auto leading-relaxed">
            Flexible memberships designed to fit your lifestyle and aesthetic goals
          </p>
        </div>

        {/* Mobile Cards */}
        <div className="block lg:hidden space-y-6 mb-12">
          {tiers.map((tier, index) => <Card key={index} className={`relative border-2 ${tier.popular ? 'border-juv-terracotta' : 'border-juv-rose-clay/20'} shadow-luxury`}>
              {tier.popular && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-juv-terracotta text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>}
              <CardHeader className="text-center pb-4">
                <div className={`w-16 h-16 bg-${tier.color}/10 rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <tier.icon className={`w-8 h-8 text-${tier.color}`} />
                </div>
                <CardTitle className="text-2xl font-bold text-juv-deep-chestnut">{tier.name}</CardTitle>
                <div className="text-3xl font-bold text-juv-terracotta">{tier.price}<span className="text-lg text-juv-deep-chestnut/60">/month</span></div>
                <p className="text-juv-deep-chestnut/60">{tier.description}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  {tier.benefits.map((benefit, idx) => <div key={idx} className="flex items-center">
                      <Check className="w-5 h-5 text-juv-terracotta mr-3 flex-shrink-0" />
                      <span className="text-juv-deep-chestnut">{benefit}</span>
                    </div>)}
                </div>
                <Button asChild className={`w-full bg-${tier.color} hover:bg-juv-deep-chestnut text-white`}>
                  <Link to="/membership/signup">Get Started</Link>
                </Button>
              </CardContent>
            </Card>)}
        </div>

        {/* Desktop Comparison Table */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div></div>
            {tiers.map((tier, index) => <Card key={index} className={`relative border-2 ${tier.popular ? 'border-juv-terracotta' : 'border-juv-rose-clay/20'} shadow-luxury`}>
                {tier.popular && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-juv-terracotta text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>}
                <CardHeader className="text-center bg-slate-50">
                  <div className={`w-16 h-16 bg-${tier.color}/10 rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <tier.icon className={`w-8 h-8 text-${tier.color}`} />
                  </div>
                  <CardTitle className="text-2xl font-bold text-juv-deep-chestnut">{tier.name}</CardTitle>
                  <div className="text-3xl font-bold text-juv-terracotta">{tier.price}<span className="text-lg text-juv-deep-chestnut/60">/month</span></div>
                  <p className="text-sm text-juv-deep-chestnut/60">{tier.description}</p>
                  <Button asChild className={`bg-${tier.color} hover:bg-juv-deep-chestnut text-white`}>
                    <Link to="/membership/signup">Get Started</Link>
                  </Button>
                </CardHeader>
              </Card>)}
          </div>

          {/* Feature Comparison */}
          <Card className="border-juv-rose-clay/20">
            <CardContent className="p-0">
              {features.map((feature, index) => <div key={index} className={`grid grid-cols-4 gap-6 p-4 ${index % 2 === 0 ? 'bg-juv-soft-blush/20' : 'bg-white'}`}>
                  <div className="font-medium text-juv-deep-chestnut">{feature}</div>
                  {tiers.map((tier, tierIndex) => <div key={tierIndex} className="flex justify-center">
                      {feature === "Monthly Credits" && tier.credits ? <span className="text-juv-terracotta font-semibold">{tier.credits.split(' ')[0]}</span> : feature === "Additional Service Discount" ? <span className="text-juv-terracotta font-semibold">
                          {tier.name === "Essential" ? "10%" : tier.name === "Signature" ? "15%" : "20%"}
                        </span> : tier.benefits.some(benefit => benefit.toLowerCase().includes(feature.toLowerCase())) ? <Check className="w-5 h-5 text-juv-terracotta" /> : <X className="w-5 h-5 text-gray-300" />}
                    </div>)}
                </div>)}
            </CardContent>
          </Card>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-juv-deep-chestnut/70 mb-6">
            Not sure which membership is right for you? Take our quiz to get a personalized recommendation.
          </p>
          <Button asChild size="lg" variant="outline" className="border-juv-terracotta text-juv-terracotta hover:bg-juv-terracotta hover:text-white">
            <Link to="/quiz">Take the Membership Quiz</Link>
          </Button>
        </div>
      </div>
    </section>;
};
export default MembershipComparison;